/**
 * RoomCreationFormContainer wires lobby signals to the RoomCreationForm UI.
 *
 * Why: The form needs reactive state from lobby signals. This container
 * handles the signal subscriptions and passes plain values as props.
 *
 * When to use: Used by App.tsx when user is creating a new room.
 */
import { useSignals } from '@preact/signals-react/runtime'
import { RoomCreationForm } from './RoomCreationForm'
import type { RoomDep } from '../../../deps/room/room.types'
import type { Lobby } from '../../../deps/lobby/lobby.types'
import type { AuthDep } from '../../../deps/auth/auth.types'
import type { LocalMates } from '../../../deps/localMates/localMates/localMates.types'

interface RoomCreationFormContainerProps {
  roomConnection: RoomDep['connection']
  lobby: Lobby
  auth: AuthDep
  localMates: LocalMates
}

export function RoomCreationFormContainer({ roomConnection, lobby, auth, localMates }: RoomCreationFormContainerProps) {
  useSignals()

  const handleCreate = async () => {
    try {
      const roomName = lobby.newRoomName.value.trim()
      if (!roomName) return

      if (!localMates.localMates.value[0]?.name) {
        lobby.pendingRoomEntryAction.value = () => handleCreate()
        lobby.roomMode.value = 'name-entry'
        return
      }

      const user = auth.currentUser.value
      const ownerName = user ? (user.fullName || user.username || user.firstName || 'Someone') : 'Someone'

      lobby.pendingRoomEntryAction.value = null
      const roomCode = await roomConnection.createRoom(roomName, ownerName)
      await roomConnection.joinRoom(roomCode)
      lobby.roomMode.value = 'room'
    } catch (e) {
      // error is handled and displayed inside roomConnection
    }
  }

  return (
    <RoomCreationForm
      roomName={lobby.newRoomName.value || ''}
      error={roomConnection.error.value || ''}
      onRoomNameChange={(name) => lobby.newRoomName.value = name}
      onCreate={handleCreate}
      onCancel={() => lobby.roomMode.value = 'room-selection'}
    />
  )
}
